import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useAthleteMode } from "./AthleteModeContext";
import { fetchAthleteDeal, openAthleteThread, respondToOffer } from "./athleteServices";
import { fmtMoney } from "./athleteUtils";
import { fmtDate, fmtLongRange, timeAgo } from "../../ForBrands/brandDealUtils";
import DeclineModal from "./DeclineModal";
import { ActionBar, BackLink, Badge, Btn, Card, ErrorState, InsightBox, Loading, Overline } from "../../ForBrands/ui/BrandUI";
import { MobileCard } from "../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import OfferMobile, { DealScreen } from "./mobile/OfferMobile";

// Athlete Mode · offer view (Figma "Athlete offer view"): the proposed scope
// of work from a brand, the Cache AI comparison, and the accept / counter /
// ask / decline actions. Guardian banner shows for athletes under 18.
// On phones (inside the Athlete Mode shell, below 640px) the page renders
// OfferMobile instead (Figma 2000:989): same data, state and handlers.

const Term = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-[6px] ${className}`}>
    <Overline>{label}</Overline>
    {children}
  </div>
);

// " · expires in N days" after "sent ...". The clause is left out when the
// server sends no day count (null or missing), as the brand's offer pages do.
const expiresIn = (days) => (days == null ? "" : ` · expires in ${days} day${days === 1 ? "" : "s"}`);

const AthleteOfferPage = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { refreshBadges } = useAthleteMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const phone = usePhoneTheme();
  const location = useLocation();

  // Phone back: the previous in-app screen when there is one, else My Deals
  // (a shared link opens the offer with no history to go back to; so does a
  // counter opened from a link, which swaps itself for this offer and marks
  // it `firstEntry`).
  const goBack = () => {
    if (location?.key && location.key !== "default" && !location.state?.firstEntry) navigate(-1);
    else navigate("/athlete/deals");
  };

  const load = async () => {
    try {
      const payload = await fetchAthleteDeal(dealId);
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load this offer");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const accept = async () => {
    setBusy(true);
    try {
      await respondToOffer(dealId, { action: "accept" });
      toast.success("Offer accepted. The brand will send the contract next.");
      refreshBadges();
      navigate("/athlete/deals");
    } catch (err) {
      toast.error(err.message || "Could not accept the offer");
    } finally {
      setBusy(false);
    }
  };

  const decline = async (reason) => {
    setBusy(true);
    try {
      await respondToOffer(dealId, { action: "decline", reason });
      toast.success("Offer declined.");
      refreshBadges();
      navigate("/athlete/deals");
    } catch (err) {
      toast.error(err.message || "Could not decline the offer");
    } finally {
      setBusy(false);
      setDeclining(false);
    }
  };

  const ask = async () => {
    setBusy(true);
    try {
      const thread = await openAthleteThread(dealId);
      navigate(`/athlete/messages?thread=${thread.id}`);
    } catch (err) {
      toast.error(err.message || "Could not open the conversation");
      setBusy(false);
    }
  };

  if (error) {
    if (phone) {
      return (
        <DealScreen title="Offer" onBack={goBack}>
          <ErrorState>{error}</ErrorState>
        </DealScreen>
      );
    }
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) {
    if (phone) {
      return (
        <DealScreen title="Offer" onBack={goBack}>
          <Loading />
        </DealScreen>
      );
    }
    return <Loading />;
  }

  const { deal, campaign, brand, athlete, guardian, rep, comparison, exclusivityText } = data;
  const offer = deal.offer;
  const counter = deal.counter;
  const open = deal.status === "offer_sent";
  const countered = deal.status === "countered";

  if (!offer) {
    if (phone) {
      return (
        <DealScreen title="Offer" onBack={goBack}>
          <ErrorState>This deal has no offer attached yet.</ErrorState>
        </DealScreen>
      );
    }
    return (
      <div className="py-10">
        <ErrorState>This deal has no offer attached yet.</ErrorState>
      </div>
    );
  }

  if (!open && !countered) {
    const href = ["accepted", "contract_sent"].includes(deal.status) ? `/athlete/deals/${dealId}/contract` : `/athlete/deals/${dealId}`;
    if (phone) {
      return (
        <DealScreen
          title="Offer"
          onBack={goBack}
          actions={
            <Btn size="lg" className="w-full" onClick={() => navigate(href)}>
              Open the deal
            </Btn>
          }
        >
          <MobileCard className="items-start">
            <Badge tone="outline">{deal.statusLabel}</Badge>
            <p className="bm-body-m text-bm-muted">This offer is no longer open for a response.</p>
          </MobileCard>
        </DealScreen>
      );
    }
    return (
      <div className="flex flex-col gap-4 pt-6">
        <BackLink to="/athlete/deals">Back to My Deals</BackLink>
        <Card className="flex flex-col items-start gap-3">
          <Badge tone="outline">{deal.statusLabel}</Badge>
          <p className="bm-body-m text-bm-muted">This offer is no longer open for a response.</p>
          <Btn onClick={() => navigate(href)}>Open the deal</Btn>
        </Card>
      </div>
    );
  }

  const termsTitle = countered ? "Your counter is with the brand" : `Proposed Scope of Work from ${brand.company}`;
  const stateLabel = countered ? "Counter sent" : "New offer";
  const declineModal = declining && <DeclineModal onClose={() => setDeclining(false)} onConfirm={decline} busy={busy} />;

  if (phone) {
    return (
      <OfferMobile
        onBack={goBack}
        stateLabel={stateLabel}
        termsTitle={termsTitle}
        subtitle={
          countered
            ? `Counter sent ${timeAgo(counter?.at)} · the brand can accept, decline or reply`
            : `sent ${timeAgo(offer.sentAt)}${expiresIn(deal.offerDaysLeft)}`
        }
        exclusivityTitle={
          offer.exclusivityEnabled === false || !offer.exclusivityDays
            ? "No exclusivity"
            : `${offer.exclusivityDays}-day ${offer.exclusivityScope === "all_brands" ? "all-brand" : "category"} restriction`
        }
        brandLine={`${brand.verified ? "Verified brand" : "New brand"} · ${brand.completedCampaigns} completed campaign${
          brand.completedCampaigns === 1 ? "" : "s"
        } on Cache${brand.fullName ? ` · contact ${brand.fullName}` : ""}`}
        deal={deal}
        campaign={campaign}
        brand={brand}
        athlete={athlete}
        guardian={guardian}
        rep={rep}
        comparison={comparison}
        exclusivityText={exclusivityText}
        open={open}
        countered={countered}
        busy={busy}
        onAccept={accept}
        onCounter={() => navigate(`/athlete/deals/${dealId}/counter`, { state: { fromOffer: true } })}
        onAsk={ask}
        onDecline={() => setDeclining(true)}
        modal={declineModal}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6 pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <BackLink to="/athlete/home">Back to notifications</BackLink>
        <span className="flex flex-wrap items-center gap-3 bm-body-m text-bm-muted">
          <span>
            {campaign?.name} · {stateLabel}
          </span>
          <span className="h-4 w-px bg-bm-border" />
          <span>
            {brand.company} · {fmtLongRange(offer.startDate, offer.endDate)}
          </span>
        </span>
      </div>

      <div className="flex flex-col gap-[6px]">
        <Overline>Deal flow</Overline>
        <h1 className="bm-h-l text-bm-ink">{termsTitle}</h1>
        <p className="bm-body-l text-bm-muted">
          {countered
            ? `Counter sent ${timeAgo(counter?.at)} · the brand can accept, decline or reply`
            : `sent ${timeAgo(offer.sentAt)}${expiresIn(deal.offerDaysLeft)}`}
        </p>
      </div>

      {athlete.isMinor && (
        <div className="flex flex-wrap items-center gap-4 rounded-[12px] bg-bm-mutedbg px-5 py-[14px]">
          <Badge tone="dark">Guardian required</Badge>
          <span className="bm-body-m text-bm-ink">
            Under 18 · A parent or legal guardian's signature is required to co-sign this contract.{" "}
            {guardian
              ? `${guardian.name} will be asked to co-sign after you.`
              : "Add a parent or guardian to your Circle before signing."}
            {!guardian && (
              <>
                {" "}
                <Link to="/athlete/circle" className="text-bm-orange underline underline-offset-2">
                  Open My Circle
                </Link>
              </>
            )}
          </span>
        </div>
      )}

      <Card padding="p-8" className="flex flex-col gap-8">
        <div className="flex flex-col gap-1 border-b border-bm-border pb-7">
          <div className="flex flex-wrap items-center gap-3">
            <span className="bm-h-m text-bm-ink">{brand.company}</span>
            {brand.verified ? (
              <Badge tone="outline" dot="#1f9d55">Verified brand</Badge>
            ) : (
              <Badge tone="passive">New to Cache</Badge>
            )}
          </div>
          <span className="bm-body-m text-bm-muted">
            {brand.verified ? "Verified brand" : "New brand"} · {brand.completedCampaigns} completed campaign{brand.completedCampaigns === 1 ? "" : "s"} on Cache
            {brand.fullName ? ` · contact ${brand.fullName}` : ""}
          </span>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          <div className="flex flex-col gap-8">
            <Term label="Guaranteed compensation">
              <span className="bm-num-l text-bm-ink">{fmtMoney(offer.compensation, { cents: true })}</span>
              <span className="bm-body-m text-bm-muted">You earn the full amount. The brand pays all Cache and transfer fees.</span>
            </Term>
            <Term label="Deliverables list">
              <ul className="flex flex-col gap-2">
                {(offer.deliverableLines || []).map((line) => (
                  <li key={line} className="flex items-start gap-2 bm-body-m text-bm-ink">
                    <span className="mt-[8px] h-[5px] w-[5px] shrink-0 rounded-full bg-bm-ink" />
                    {line}
                  </li>
                ))}
              </ul>
            </Term>
          </div>
          <div className="flex flex-col gap-8">
            <Term label="Campaign timeline & payments">
              <span className="bm-body-m text-bm-ink">{fmtLongRange(offer.startDate, offer.endDate)}</span>
              <ul className="flex flex-col gap-1">
                {(offer.milestones || []).map((m) => (
                  <li key={m.id} className="bm-body-m text-bm-muted">
                    {fmtMoney(m.amount)} {m.label}
                  </li>
                ))}
              </ul>
            </Term>
            <Term label="Exclusivity window">
              <span className="bm-h-xs text-bm-ink">
                {offer.exclusivityEnabled === false || !offer.exclusivityDays
                  ? "No exclusivity"
                  : `${offer.exclusivityDays}-day ${offer.exclusivityScope === "all_brands" ? "all-brand" : "category"} restriction`}
              </span>
              <span className="bm-body-m text-bm-muted">{exclusivityText}</span>
            </Term>
          </div>
        </div>

        {countered && counter && (
          <div className="rounded-[8px] border border-bm-border bg-bm-subtle p-5">
            <Overline className="pb-3">Your counter</Overline>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <div className="bm-body-s text-bm-muted">Compensation</div>
                <div className="bm-h-s text-bm-ink">
                  {fmtMoney(counter.compensation, { cents: true })}{" "}
                  {deal.counterDiff?.compensation && <Badge tone="outline">{deal.counterDiff.compensation}</Badge>}
                </div>
              </div>
              <div>
                <div className="bm-body-s text-bm-muted">Exclusivity</div>
                <div className="bm-h-s text-bm-ink">
                  {counter.exclusivityDays} days {deal.counterDiff?.exclusivity && <Badge tone="dark">{deal.counterDiff.exclusivity}</Badge>}
                </div>
              </div>
              <div>
                <div className="bm-body-s text-bm-muted">Timeline</div>
                <div className="bm-h-s text-bm-ink">
                  {fmtDate(counter.startDate, { year: false })} – {fmtDate(counter.endDate)}
                </div>
              </div>
            </div>
            {counter.note && <p className="mt-3 bm-body-m text-bm-muted">“{counter.note}”</p>}
          </div>
        )}
      </Card>

      <InsightBox title="Cache AI comparison">{comparison}</InsightBox>

      {rep && (
        <p className="flex items-center gap-2 bm-body-m text-bm-muted">
          <span className="h-[6px] w-[6px] rounded-full bg-bm-ink" />
          {rep.name} (your rep) has access to this thread and all deal terms.
        </p>
      )}

      <ActionBar
        left={
          open ? (
            <>
              <Btn onClick={accept} disabled={busy}>
                {busy ? "Working..." : "Accept offer"}
              </Btn>
              <Btn variant="secondary" onClick={() => navigate(`/athlete/deals/${dealId}/counter`)} disabled={busy}>
                Counter offer
              </Btn>
              <button type="button" onClick={ask} disabled={busy} className="px-3 bm-label-l text-bm-muted hover:text-bm-ink">
                Ask a question
              </button>
            </>
          ) : (
            <>
              <Btn variant="secondary" onClick={() => navigate(`/athlete/deals/${dealId}/counter`)} disabled={busy}>
                Revise counter
              </Btn>
              <button type="button" onClick={ask} disabled={busy} className="px-3 bm-label-l text-bm-muted hover:text-bm-ink">
                Message the brand
              </button>
            </>
          )
        }
        right={
          <Btn variant="secondary" className="!text-bm-critical" onClick={() => setDeclining(true)} disabled={busy}>
            Decline
          </Btn>
        }
      />

      {declineModal}
    </div>
  );
};

export default AthleteOfferPage;
